import {
  Text,
  Box,
  Button,
  Tag,
  TagLabel,
  Wrap,
  WrapItem,
  useToast,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from "@chakra-ui/react";
import { PencilSimple } from "@phosphor-icons/react";
import { H2 } from "components/text/headings";
import { useEffect, useMemo, useState } from "react";
import { useRerender } from "rerenderer";
import { useQuery, useQueryClient } from "react-query";
import { showErrorToast } from "utils";
import questionnaireService from "api/issb/questionnaire.service";
import { useAuth } from "context";
import { issbKeys } from "hooks/query";
import { round_as_source_sync_questions } from "api/ai/ai.service";
import { isFoundationLikeRound } from "utils/roundFilters";
/**
 * Select source rounds - and update the Round object and save it.
 *
 * IF selectedRounds / setSelectedRounds are not provided, then the component will work on round.sourceRoundIds.
 *
 * Note: likely to be redesigned
 *
 * @param {*} param0
 * @returns
 */
export default function SourcesRoundsAsSourceSelector({
  currentRoundId,
  selectedRounds,
  setSelectedRounds,
  allowedRoundIds,
  hideAccordion,
  hideHeader,
}) {
  const { getAccessTokenSilently } = useAuth();
  const queryClient = useQueryClient();
  const { data: roundsListData } = useQuery(
    issbKeys.questionnaireRound(),
    async () => {
      await getAccessTokenSilently();
      return questionnaireService.list({});
    },
    {
      enabled: true,
    }
  );

  let roundOptions = [];
  if (roundsListData?.content) {
    roundOptions = roundsListData?.content
      ?.filter((r) => r.id !== currentRoundId)
      .filter((r) => !isFoundationLikeRound(r))
      .filter((r) => !allowedRoundIds || allowedRoundIds.includes(r.id))
      .map((r) => ({ label: r.name, value: r.id }));
  }

  const toast = useToast();

  const { data: questionnaireData } = useQuery(
    currentRoundId,
    async () => {
      await getAccessTokenSilently();
      return questionnaireService.get(currentRoundId);
    },
    {
      enabled: !!currentRoundId,
    }
  );
  // Use questionnaireData?.sourceRoundIds directly?
  // Default to all rounds only if sourceRoundIds was never set (null/undefined)
  const allRoundIds = useMemo(
    () => roundOptions.map((o) => o.value),
    // roundOptions is derived from roundsListData + currentRoundId + allowedRoundIds, no need to list it separately
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [roundsListData, currentRoundId, allowedRoundIds]
  );
  if (!setSelectedRounds || !selectedRounds) {
    selectedRounds = questionnaireData?.sourceRoundIds != null ? questionnaireData.sourceRoundIds : allRoundIds;
  }

  const [editing, setEditing] = useState(false);
  const { rerender } = useRerender();

  useEffect(() => {
    if (questionnaireData && setSelectedRounds) {
      const saved = questionnaireData?.sourceRoundIds;
      setSelectedRounds(saved != null ? saved : hideAccordion ? [] : allRoundIds);
    }
  }, [questionnaireData, setSelectedRounds, allRoundIds, hideAccordion]);

  const applyChange = (roundIds) => {
    if (setSelectedRounds) {
      setSelectedRounds(roundIds);
    } else {
      questionnaireData["sourceRoundIds"] = roundIds;
      questionnaireService
        .update(currentRoundId, questionnaireData, {})
        .catch((err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message));
      queryClient.invalidateQueries(issbKeys.questionnaireRound(currentRoundId));
      for (const roundId of roundIds) {
        round_as_source_sync_questions(roundId);
      }
    }
    rerender();
  };

  const handleToggleRound = (roundId) => {
    const newIds = selectedRounds.includes(roundId)
      ? selectedRounds.filter((id) => id !== roundId)
      : [...selectedRounds, roundId];
    applyChange(newIds);
  };

  const handleSelectAll = () => {
    const allIds = roundOptions.map((o) => o.value);
    const newIds = selectedRounds.length === roundOptions.length ? [] : allIds;
    applyChange(newIds);
  };

  const roundsContent = (
    <>
      {!hideHeader && (
        <Text mb={4} color="gray.500">
          {hideAccordion
            ? "Select other rounds to be used as a reference source for this review."
            : "Select which rounds are available to users when running AI reviews."}
        </Text>
      )}
      {!hideAccordion && selectedRounds.length === roundOptions.length && !editing ? (
        <Wrap spacing={2} align="center">
          <WrapItem>
            <Tag
              size="sm"
              borderRadius="full"
              bg="lightLime"
              border="1px solid"
              borderColor="lime"
              px={3}
              py={1}
              cursor="pointer"
              onClick={() => setEditing(true)}
            >
              <TagLabel>All rounds selected</TagLabel>
              <Box as="span" ml={2} display="inline-flex">
                <PencilSimple size={12} />
              </Box>
            </Tag>
          </WrapItem>
        </Wrap>
      ) : (
        <Wrap spacing={2} align="center">
          {roundOptions.map((option) => {
            const isSelected = selectedRounds.includes(option.value);
            return (
              <WrapItem key={option.value}>
                <Tag
                  size="sm"
                  borderRadius="full"
                  bg={isSelected ? "lightLime" : "gray.100"}
                  border="1px solid"
                  borderColor={isSelected ? "lime" : "gray.300"}
                  color={isSelected ? "inherit" : "gray.400"}
                  px={3}
                  py={1}
                  cursor="pointer"
                  onClick={() => handleToggleRound(option.value)}
                >
                  <TagLabel noOfLines={1}>{option.label}</TagLabel>
                  <Box as="span" ml={1} fontSize="xs">
                    {isSelected ? "✕" : "+"}
                  </Box>
                </Tag>
              </WrapItem>
            );
          })}
          {roundOptions.length > 5 && (
            <>
              <WrapItem>
                <Button
                  variant="link"
                  size="xs"
                  color="gray.500"
                  fontWeight="normal"
                  onClick={() => {
                    handleSelectAll();
                    setEditing(false);
                  }}
                >
                  Select all
                </Button>
              </WrapItem>
              <WrapItem>
                <Button variant="link" size="xs" color="gray.500" fontWeight="normal" onClick={() => applyChange([])}>
                  Deselect all
                </Button>
              </WrapItem>
            </>
          )}
        </Wrap>
      )}
    </>
  );

  if (hideAccordion) {
    if (hideHeader) {
      return <Box>{roundsContent}</Box>;
    }
    return (
      <Box mt={8}>
        <H2>
          Rounds{" "}
          <Text as="span" fontWeight="normal" color="gray.500">
            ({selectedRounds.length}/{roundOptions.length})
          </Text>
        </H2>
        {roundsContent}
      </Box>
    );
  }

  return (
    <Accordion allowToggle mt={8}>
      <AccordionItem borderBottom="none">
        <AccordionButton px={0} py={4} _hover={{ bg: "transparent" }}>
          <H2 mt={0} mb={0}>
            Rounds{" "}
            <Text as="span" fontWeight="normal" color="gray.500">
              ({selectedRounds.length}/{roundOptions.length})
            </Text>
          </H2>
          <AccordionIcon mr={0} ml="auto" boxSize={6} />
        </AccordionButton>
        <AccordionPanel px={0}>{roundsContent}</AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
}
